<script setup lang="ts">
import { computed, defineProps, reactive, ref } from 'vue'
import { routerPush } from '@/router'

import { api, isFetchError } from '@/services'
import type { FormItemError, LoginAccount } from '@/services/api'
import { required } from '@/utils/helpers-validation'

import DialogShowLostPasswordDialog from '@/components/user/LostPasswordForm.vue'

import { useUserStore } from '@/stores/user'
import { useGlobalStore } from '@/stores/global'
import ErrorDisplayHelper from '@/components/helpers/ErrorDisplayHelper.vue'
import { processApiAndCartErrors } from '@/utils/helpers-api'

const globalStore = useGlobalStore()

interface Props {
  isModal?: boolean
}

const props = defineProps<Props>()
const isModal = ref(props.isModal ?? false)

const emit = defineEmits(['close'])

const formRef = ref<HTMLFormElement | null>(null)
const showPassword = ref<boolean | undefined>()

// used in form
const loading = computed(() => {
  return globalStore.loadingCounter > 0
})

const form: LoginAccount = reactive({
  email: '',
  password: ''
})

// ref<Boolean | null>(false)
const { updateUser, updateAuth } = useUserStore()

/** dialog / modal **/
const showLostPasswordDialog = ref(false)

function openLostPasswordModal() {
  showLostPasswordDialog.value = true
}

function navigateToCreateAccount() {
  if (isModal.value) {
    emit('close', false)
  }
  routerPush('create-account')
}

const haveErrors = computed(() => {
  return errors.value.length > 0
})
const errors = ref<FormItemError[]>([])

const login = async () => {
  errors.value = []
  const { valid } = await formRef.value!.validate()
  if (!valid) {
    errors.value.push({
      message: 'Something not right with your form. Please review and resubmit.'
    })

    return
  }

  try {
    globalStore.incrementLoading()
    const result = await api.api.loginAccount({ data: form })

    updateUser(result.data.data?.account)
    api.setSecurityData(result.data.authorization?.access_token)
    updateAuth(result.data.authorization)

    // set the cart to the saved cart in account data
    // TODO where should this function go?
    // const cartItems = result.data.data?.account?.cart?.items
    // cartStore.resetCart()
    // if (Array.isArray(cartItems)) {
    //   cartItems.forEach(async (cartItem) => {
    //     try {
    //       const result = await api.api.validateProduct({ data: cartItem })
    //       const cartProduct = result.data.data as InvoiceRow
    //       const canAddResult = cartStore.canAddItemToCart(cartProduct.cart_item as CartItem)
    //       if (canAddResult && cartProduct) {
    //         cartStore.addItemToCart(cartProduct)
    //       }
    //     } catch (error) {
    //       // just don't add this item to the cart
    //     }
    //   })
    // }

    const fn = result.data.data?.account?.first_name ?? ''
    globalStore.addSnackbarMessageToQueue('Welcome back, ' + fn, 'primary')

    if (isModal.value) {
      emit('close', false)
    }

    await routerPush('home')
  } catch (error) {
    if (isFetchError(error)) {
      errors.value = processApiAndCartErrors(error, 'login')
    }
    // console.error(error)
  } finally {
    globalStore.decrementLoading()
  }
}

const dynamicClasses = computed(() => {
  let classes = 'dialog-form'
  if (isModal.value) {
    classes += ' dialog-modal'
  }
  return classes
})
</script>
<template>
  <v-card class="ma-0 pa-0 dialog-form mx-auto" :class="dynamicClasses" variant="flat">
    <!--  <v-card class="mx-auto px-6 py-8 dialog-form">-->
    <v-card-text>
      <v-row>
        <v-col>
          <ErrorDisplayHelper :have-errors="haveErrors" :errors="errors"></ErrorDisplayHelper>
        </v-col>
      </v-row>

      <v-form ref="formRef" @submit.prevent="login">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="form.email"
              :readonly="loading"
              :rules="[required]"
              class="mb-2"
              placeholder="Email address"
              label="Email"
              variant="outlined"
              prepend-inner-icon="mdi-email-outline"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.password"
              :readonly="loading"
              :rules="[required]"
              label="Password"
              placeholder="Enter your password"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              prepend-inner-icon="mdi-lock-outline"
              :type="showPassword ? 'text' : 'password'"
              hint="At least 8 characters"
              variant="outlined"
              @click:append-inner="showPassword = !showPassword"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div class="d-flex flex-wrap ga-3 justify-center mb-6">
              <v-btn
                :disabled="!formRef"
                :loading="loading"
                size="large"
                type="submit"
                variant="elevated"
                class="p-4 bg-orange text-white text-bold"
                v-tooltip="'Sign in'"
              >
                Sign In
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-form>

      <v-row></v-row>
    </v-card-text>
    <v-card-actions class="pt-4">
      <v-btn
        variant="elevated"
        class="p-4 bg-lightblue text-white text-bold"
        v-tooltip="'Click here to reset your password.'"
        @click="openLostPasswordModal()"
        >Forgot Password?
      </v-btn>

      <v-spacer />

      <v-btn
        variant="elevated"
        class="p-4 bg-lightblue text-white text-bold"
        v-tooltip="'Click here to create a new account'"
        @click="navigateToCreateAccount()"
        >Create Account
      </v-btn>
    </v-card-actions>
  </v-card>

  <DialogShowLostPasswordDialog
    v-model:modalValue="showLostPasswordDialog"
  ></DialogShowLostPasswordDialog>
</template>
